import type * as stripeJs from '@stripe/stripe-js'
import { colors } from '@unocss/preset-mini/colors'
import { isClient } from '@vueuse/core'

export const isDarkMode = () => isClient && window.document.documentElement.classList.contains('dark-mode')

export default defineAppConfig({
  // The announcement bar (`AnnouncementStrip.vue`, fixed at the top of every
  // default-layout page) is `z-60`, so the default `z-50` modal layers
  // (sheet/dialog) render underneath it. Bump them above the bar (but below
  // toasts, see `toast.slots.viewport`) so drawers like "add address" are never covered.
  ui: {
    dialog: {
      slots: {
        // No fade with reduced motion, as the sheet's own panel (`sheet.slots.root`): a fading overlay
        // outlived the panel, and its unmount then cleared the body scroll lock of whatever opened next
        // (the cart, after a shared list's "Přidat vše do košíku").
        overlay: 'fixed inset-0 z-70 data-[state=closed]:animate-out data-[state=open]:animate-in bg-black/80 data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none',
        content: 'fixed left-1/2 top-1/2 z-70 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
        scrollContentOverlay: 'fixed inset-0 z-70 grid data-[state=closed]:animate-out data-[state=open]:animate-in place-items-center overflow-y-auto bg-black/80',
        scrollContent: 'relative z-70 grid w-full max-w-lg my-8 gap-4 border border-border bg-background p-6 shadow-lg duration-200 sm:rounded-lg md:w-full',
      },
    },
    // AlertDialogContent renders under the DIALOG overlay slot above, so the
    // alert dialog's own content has to be bumped with it: left at the layer's
    // `z-50`, every confirm dialog (address delete, the checkout channel
    // switch) sat dimmed beneath its own backdrop with unclickable buttons.
    alertDialog: {
      slots: {
        content: 'fixed left-1/2 top-1/2 z-70 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
      },
    },
    sheet: {
      slots: {
        root: 'fixed z-70 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500 motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none',
      },
    },
    // Keep success/error toasts in the neutral ink/page palette rather than green/red.
    // Success reads as a filled ink confirmation; error is a quieter warm-outlined card.
    toast: {
      slots: {
        // Inherit the toast's text colour so the dismiss icon stays legible on the
        // dark (success) and light (default/error) variants alike.
        close: 'absolute right-2 top-2 rounded-md p-1 text-current opacity-0 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none group-hover:opacity-100',
        // The layer's viewport at `z-[160]` instead of `z-[100]`: toasts are the top layer, above
        // the cart drawer (z-100, teleported later in <body>, so it painted over a z-100 toast),
        // the gift card (120/121), the gift pickers (130) and quick add (150). A failed add from
        // the drawer's "pair with" rail can then say why while the cart stays open.
        viewport: 'fixed top-0 z-[160] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
      },
      variants: {
        variant: {
          default: 'border border-[var(--line)] bg-[var(--page)] text-[var(--ink)]',
          success: 'group border border-[var(--ink)] bg-[var(--ink)] text-white',
          destructive: 'group border border-[var(--ink)] bg-[var(--bg)] text-[var(--ink)]',
        },
      },
    },
  },

  // Where "Continue shopping" sends a customer out of an empty cart, checkout
  // or wishlist. `collectionSlug` is resolved under /collections/, and is the
  // destination whenever no better one is known; set
  // `preferLastVisitedCategory: false` to always send them there instead of
  // back to the category they were last browsing.
  // Pick a slug that always resolves: an unknown one falls through to
  // collections/[...slug].vue, which finds no matching Vendure collection and
  // renders a permanently empty grid rather than a 404.
  continueShopping: {
    collectionSlug: 'best-sellers',
    preferLastVisitedCategory: true,
  },

  stripe: {
    elements: {
      options: {
        fonts: [
          { family: 'Gilam', src: 'url(/fonts/gilam-regular.woff2)', weight: '400' },
          { family: 'Gilam', src: 'url(/fonts/gilam-semi-bold.woff2)', weight: '600' },
          { family: 'Gilam', src: 'url(/fonts/gilam-bold.woff2)', weight: '700' },
        ],
      },
    },

    card: {
      // hidePostalCode: true,
      classes: {
        base: '',
        complete: '',
        empty: '',
        focus: '',
        invalid: '',
        webkitAutofill: '',
      },
      style: {
        base: {
          'backgroundColor': 'transparent',
          'color': isDarkMode() ? colors.slate[700] : colors.slate[900],
          'fontFamily': 'Gilam,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
          'fontSize': '16px',
          'fontWeight': '500',
          // 'fontSize': '32px',
          'fontSmoothing': 'antialiased',
          'lineHeight': '20px',
          //
          'iconColor': isDarkMode() ? colors.slate[400] : colors.slate[500],
          //
          ':hover': {},
          ':focus': {
            color: isDarkMode() ? colors.slate[700] : colors.slate[900],
          },
          '::placeholder': {
            color: isDarkMode() ? colors.slate[400] : colors.slate[500],
          },
          '::selection': {},
          ':-webkit-autofill': {},
          ':disabled': {
            color: colors.slate[500],
          },
        },
        complete: {},
        empty: {},
        invalid: {
          'iconColor': isDarkMode() ? colors.red[400] : colors.rose[400],
          'color': isDarkMode() ? colors.rose[200] : colors.red[900],
          '::placeholder': {
            color: isDarkMode() ? colors.red[400] : colors.rose[400],
          },
        },
      },
    } satisfies stripeJs.StripeCardElementOptions,

    p24Bank: {
      style: {
        base: {
          'color': isDarkMode() ? colors.slate[700] : colors.slate[900],
          'fontFamily': 'Gilam,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
          'fontSize': '16px',
          'fontSmoothing': 'antialiased',
          'padding': '8px',
          ':hover': {
            color: isDarkMode() ? colors.slate[700] : colors.slate[900],
          },
          ':focus': {
            color: isDarkMode() ? colors.slate[700] : colors.slate[900],
          },
          '::placeholder': {
            color: isDarkMode() ? colors.slate[400] : colors.slate[500],
          },
          ':disabled': {
            color: colors.slate[500],
          },
        },
        complete: {
          color: isDarkMode() ? colors.slate[700] : colors.slate[900],
          iconColor: isDarkMode() ? colors.slate[400] : colors.slate[500],
        },
        invalid: {
          'iconColor': isDarkMode() ? colors.red[400] : colors.rose[400],
          'color': isDarkMode() ? colors.rose[200] : colors.red[900],
          '::placeholder': {
            color: isDarkMode() ? colors.red[400] : colors.rose[400],
          },
        },
      },
      classes: {
        base: '',
        complete: '',
        empty: '',
        focus: '',
        invalid: '',
      },
    } as stripeJs.StripeP24BankElementOptions,
  },
})
